import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithRedirect,
} from 'firebase/auth';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { auth, db, googleProvider } from 'src/services/firebase-service';
import logging from 'src/services/logging';

import {
  Backdrop,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

// TODO: move to hokage table
const REGISTRATION_OPEN = false;

const LoginPage = () => {
  const [, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location: any = useLocation();
  const [authenticating, setAuthenticating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const handleRedirect = async () => {
      const response = await getRedirectResult(auth);

      if (response) {
        const user = response.user;

        logging.info(
          `${user.displayName} successful logged in with Google.`,
          'LoginPage',
        );

        // check if user exists in db
        const q = query(collection(db, 'users'), where('uid', '==', user.uid));
        const doc = await getDocs(q);
        if (doc.docs.length === 0 && REGISTRATION_OPEN) {
          await addDoc(collection(db, 'users'), {
            uid: user.uid,
            fullName: user.displayName,
            providerId: user.providerData[0].providerId,
            email: user.email,
            isHokage: false,
          });
        }
        navigate('/');
      }
    };

    handleRedirect();
  });

  const loginWithEmailAndPassword = async (data: any) => {
    try {
      if (error !== '') setError('');

      setAuthenticating(true);

      const response = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );

      const user = response.user;

      logging.info(`${user.displayName} successful logged in.`, 'LoginPage');

      navigate(from, { replace: true });
    } catch (error: any) {
      logging.error(error, 'LoginPage');

      if (error.code.includes('auth/user-not-found')) {
        setError('User not found.');
      } else if (error.code.includes('auth/wrong-password')) {
        setError('Nope.');
      } else {
        setError('Unable to login. Please try again.');
      }

      setAuthenticating(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      if (error !== '') setError('');

      setAuthenticating(true);

      await signInWithRedirect(auth, googleProvider);
    } catch (error) {
      logging.error(error, 'LoginPage');

      setAuthenticating(false);
    }
  };

  return (
    <>
      {loading && (
        <Backdrop open={loading}>
          <CircularProgress color='inherit' />
        </Backdrop>
      )}
      <Container maxWidth='sm'>
        <Card>
          <CardHeader title='Login' />
          <Divider />
          <CardContent>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={12}>
                <Button
                  disabled={loading}
                  variant='contained'
                  fullWidth={true}
                  color='error'
                  startIcon={<GoogleIcon />}
                  onClick={loginWithGoogle}
                >
                  Sign in with Google
                </Button>
              </Grid>
              <Grid item>
                <Divider>OR</Divider>
              </Grid>
              <Grid item>
                <Formik
                  initialValues={{
                    email: '',
                    password: '',
                  }}
                  validationSchema={Yup.object().shape({
                    email: Yup.string()
                      .label('Email')
                      .email()
                      .max(50)
                      .required(),
                    password: Yup.string().label('Password').required(),
                  })}
                  onSubmit={(data) => {
                    loginWithEmailAndPassword(data);
                  }}
                >
                  {({ errors, handleChange, isValid, touched }) => (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            name='email'
                            type='email'
                            label='Email Address'
                            fullWidth={true}
                            error={!!(touched.email && errors.email)}
                            helperText={touched.email && errors.email}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name='password'
                            type='password'
                            label='Password'
                            fullWidth={true}
                            error={!!(touched.password && errors.password)}
                            helperText={touched.password && errors.password}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            disabled={authenticating || !isValid || loading}
                            variant='contained'
                            fullWidth={true}
                            type='submit'
                          >
                            {authenticating ? (
                              <CircularProgress size={24} color='inherit' />
                            ) : (
                              'Login'
                            )}
                          </Button>
                        </Grid>
                        {REGISTRATION_OPEN && (
                          <Grid item container justifyContent='end'>
                            <Typography>
                              Don't have an account?{' '}
                              <Link to='/register'>Register</Link>
                            </Typography>
                          </Grid>
                        )}
                        <Grid item container justifyContent='end'>
                          <Link to='/forgot'>Forgot password?</Link>
                        </Grid>
                        {error && (
                          <Grid item>
                            <Typography sx={{ color: '#d32f2f' }}>
                              {error}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default LoginPage;
